import { z } from "zod";

/**
 * 选品结论汇总层（用户原话："应该明确得到结论。什么时间可以卖，现在合不合适卖。为什么不合适。"）。
 *
 * 为什么单独一层：需求／竞争／价格带三条市场信号与利润核算分属两处状态
 * （`selectionAssessment.marketSignals` 与 `profitInput`），
 * 界面上没有任何一处把它们合成一句**明确的回答**。用户看到四条信号加一堆风险条目，得自己拼。
 * 这一层就是把"现在到底卖不卖、不卖是什么时候、为什么"算出来，并且只说算得出来的话。
 *
 * 四条纪律（每条都有对应用例）：
 * 1. **判不了就说判不了**，并**点名**缺哪一维。绝不把"没有来源"读成"不合适"。
 * 2. **规则判定不能反过来当证据**：`verdict` 只能把结论推向"判不了"，
 *    不能因为"平台规则没意见"就报"现在可以卖"。
 * 3. `sourced`（有可追溯依据）**不等于** `observed`（真被观察到，§6.6"有成果≠已核实"）；
 *    依据不足或未核实的一维不能算数。
 * 4. 时间窗口只认**结构化的月度分布**，且**少于两年不给窗口**：
 *    只有一年数据时说"3 月是旺季"，是把一年的偶然当成季节性。
 *
 * 这一层是**纯函数**：不读时钟（"现在"由调用方给的 `now` 决定）、不发请求、不写库。
 * 因此同一个输入永远得到同一个结论，界面与将来可能的持久化不会算出两个答案。
 */

export const SELECTION_DIMENSION_KEYS = ["demand", "competition", "price_band", "profit"] as const;
export type SelectionDimensionKey = (typeof SELECTION_DIMENSION_KEYS)[number];

/** 维度名只有这一份：结论句、缺失清单、界面标签共用，避免三处措辞漂移。 */
export const SELECTION_DIMENSION_LABELS: Record<SelectionDimensionKey, string> = {
  demand: "需求",
  competition: "竞争",
  price_band: "价格带",
  profit: "利润",
};

export const SELECTION_DECISIONS = ["sell_now", "wait", "do_not_sell", "insufficient"] as const;
export type SelectionDecision = (typeof SELECTION_DECISIONS)[number];

/**
 * 一维的状态。
 * - `supports`：有带来源的依据（市场三维）或已核算且为正（利润）
 * - `against`：有可核算的硬事实反对（目前只有"毛利为负"这一类）
 * - `insufficient`：**有**依据，但依据不足或未被观察到（`sourced` 而降级的情形）
 * - `unknown`：这一维还没有带来源的依据（缺记录，或明确标了"需外部验证"）
 */
export const SELECTION_DIMENSION_STATUSES = ["supports", "against", "insufficient", "unknown"] as const;
export type SelectionDimensionStatus = (typeof SELECTION_DIMENSION_STATUSES)[number];

export const SelectionTimingSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("now"), note: z.string().min(1).max(240) }).strict(),
  z.object({
    kind: z.literal("window"),
    /** 旺季月份（1-12），升序去重后给出。 */
    months: z.array(z.number().int().min(1).max(12)).min(1).max(12),
    note: z.string().min(1).max(240),
  }).strict(),
  z.object({ kind: z.literal("unknown"), reason: z.string().min(1).max(240) }).strict(),
]);
export type SelectionTiming = z.infer<typeof SelectionTimingSchema>;

/**
 * 照抄证据原文的上限。取 600 与 `MarketSignal.finding` **同一个上限**：
 * 这样一条合法信号照抄过来一定合法，不需要靠截断来救。
 */
export const SELECTION_FINDING_MAX = 600;
const HEADLINE_MAX = 800;
const MISSING_MAX = 400;

/**
 * 照抄原文，但**永不超限**：超了就在上限内截断并补一个省略号。
 *
 * 为什么必须有这一步：派生在**渲染期**跑 `.parse`，任何一处超限都会抛错，
 * 而 React 渲染期抛错会把**整张任务卡**带走（不是少显示一行字）。
 * 截断是**显式**的（省略号），所以不算"改写证据"——
 * 完整原文仍在信号卡的"已记录的依据"里，可逐字核对。
 */
function quote(text: string, max = SELECTION_FINDING_MAX): string {
  const trimmed = text.trim();
  return trimmed.length <= max ? trimmed : `${trimmed.slice(0, max - 1)}…`;
}

export const SelectionDimensionVerdictSchema = z.object({
  key: z.enum(SELECTION_DIMENSION_KEYS),
  status: z.enum(SELECTION_DIMENSION_STATUSES),
  /** 有依据时**照抄**证据里的说法：不改写、不总结，否则"可核实"就落空了。 */
  finding: z.string().min(1).max(SELECTION_FINDING_MAX),
  /** 这一维缺什么／为什么不算数；`supports` 时缺省。 */
  missing: z.string().min(1).max(MISSING_MAX).optional(),
}).strict();
export type SelectionDimensionVerdict = z.infer<typeof SelectionDimensionVerdictSchema>;

export const SelectionConclusionSchema = z.object({
  decision: z.enum(SELECTION_DECISIONS),
  /** 一句话回答"合不合适卖"，并带上理由。讲事实，不复述规则。 */
  headline: z.string().min(1).max(HEADLINE_MAX),
  timing: SelectionTimingSchema,
  /** 恒为四维、顺序固定（`SELECTION_DIMENSION_KEYS`），界面不必再排一次。 */
  dimensions: z.array(SelectionDimensionVerdictSchema).length(SELECTION_DIMENSION_KEYS.length),
  /** 反对的具体理由（`against` 维度的依据原文）。 */
  blocking: z.array(z.string().min(1).max(SELECTION_FINDING_MAX)).max(8),
  /** 还缺什么，逐条可执行。 */
  missing: z.array(z.string().min(1).max(MISSING_MAX)).max(8),
  decidedAt: z.string(),
}).strict();
export type SelectionConclusion = z.infer<typeof SelectionConclusionSchema>;

/** 市场信号里这一层真正读到的字段（结构式入参，避免为了算一个结论去伪造整条记录）。 */
export interface SelectionSignalInput {
  kind: "demand" | "competition" | "review_pain" | "price_band";
  finding: string;
  verification: "sourced" | "needs_external_verification";
  conclusion?: "observed" | "insufficient" | "unverified";
}

/** 利润这一维真正读到的字段。 */
export interface SelectionProfitInput {
  complete: boolean;
  currency?: string;
  revenue?: number | null;
  totalCost?: number | null;
  profit: number | null;
  marginRate: number | null;
  missingInputs: readonly string[];
}

export interface SelectionConclusionInput {
  /** 已有的初筛判定。**不重算**：只作为输入，两者不可能互相矛盾。 */
  verdict: "suitable" | "conditional" | "insufficient";
  signals: readonly SelectionSignalInput[];
  profit?: SelectionProfitInput;
  /**
   * 结构化的月度分布。缺省＝没有可核算的分布，如实说给不出窗口。
   * 生产者待补（需真实导出 CSV／多年趋势数据），所以今天的实情就是"给不出窗口"。
   */
  seasonality?: { peakMonths: readonly number[]; observedYears: number };
  /** "现在"由调用方给：这里不读时钟，否则重放与测试都看不出"现在"是几月。 */
  now: string;
}

const SIGNAL_KIND_OF: Record<Exclude<SelectionDimensionKey, "profit">, SelectionSignalInput["kind"]> = {
  demand: "demand",
  competition: "competition",
  price_band: "price_band",
};

/** 评价痛点有来源**不等于**竞争有来源：这一维只认自己那一类信号。 */
function dimensionOf(key: Exclude<SelectionDimensionKey, "profit">, signals: readonly SelectionSignalInput[]): SelectionDimensionVerdict {
  const label = SELECTION_DIMENSION_LABELS[key];
  const mine = signals.filter((signal) => signal.kind === SIGNAL_KIND_OF[key]);
  if (mine.length === 0) {
    return { key, status: "unknown", finding: quote(`${label}：还没有记录。`), missing: `这一维还没有记录。该去看一次${label}。` };
  }
  /**
   * 同一维有多条时按"依据不足优先"收敛：一条依据冲突 / 未核实就压过另一条 supports。
   * 反过来的优先级会像旧写法那样让后一条把前一条静默盖掉（P4-2 的病根）。
   */
  const downgraded = mine.some((signal) => signal.verification === "sourced" && signal.conclusion !== undefined && signal.conclusion !== "observed");
  if (downgraded) {
    const culprit = mine.find((signal) => signal.verification === "sourced" && signal.conclusion !== undefined && signal.conclusion !== "observed")!;
    return {
      key, status: "insufficient", finding: quote(culprit.finding),
      missing: "已有依据但依据不足：有可追溯的来源不等于已被观察到。",
    };
  }
  const supported = mine.filter((signal) => signal.verification === "sourced");
  const last = supported.at(-1) ?? mine.at(-1)!;
  if (supported.length === 0) {
    return { key, status: "unknown", finding: quote(last.finding), missing: "还没有带来源的依据。" };
  }
  return { key, status: "supports", finding: quote(supported.at(-1)!.finding) };
}

function profitDimension(profit: SelectionProfitInput | undefined): SelectionDimensionVerdict {
  const label = SELECTION_DIMENSION_LABELS.profit;
  if (!profit) {
    return { key: "profit", status: "unknown", finding: quote(`${label}：还没有核算。`), missing: "还没有核算：缺采购单价、物流成本与预计售价。" };
  }
  if (!profit.complete || profit.profit === null) {
    return {
      key: "profit", status: "unknown", finding: quote(`${label}：还没算完。`),
      // 缺什么列什么，**不用 0 顶替**（0 会把"不知道"伪装成一个具体数字）。
      missing: quote(profit.missingInputs.length ? `还缺${profit.missingInputs.join("、")}。` : "输入不完整，还算不出毛利。", MISSING_MAX),
    };
  }
  const unit = profit.currency ? ` ${profit.currency}` : "";
  if (profit.profit <= 0 || (profit.marginRate ?? 0) <= 0) {
    const detail = profit.revenue !== null && profit.revenue !== undefined && profit.totalCost !== null && profit.totalCost !== undefined
      ? `售价 ${profit.revenue}${unit} − 总成本 ${profit.totalCost}${unit} = 亏 ${Math.abs(profit.profit)}${unit}`
      : `毛利 ${profit.profit}${unit}`;
    return {
      key: "profit", status: "against",
      finding: quote(`按当前输入核算毛利为负：${detail}（毛利率 ${profit.marginRate ?? "未知"}%）。`),
    };
  }
  return {
    key: "profit", status: "supports",
    finding: quote(`按当前输入核算毛利为正：毛利 ${profit.profit}${unit}（毛利率 ${profit.marginRate ?? "未知"}%）。`),
  };
}

/** 月份取 UTC：这里刻意不读本地时区，否则同一份数据在不同机器上会得到不同窗口判断。 */
function utcMonth(now: string): number {
  const parsed = new Date(now);
  return Number.isNaN(parsed.getTime()) ? 0 : parsed.getUTCMonth() + 1;
}

function timingOf(seasonality: SelectionConclusionInput["seasonality"], currentMonth: number): SelectionTiming {
  if (!seasonality) {
    return { kind: "unknown", reason: "现有证据里没有可核算的月度分布，给不出一年的售卖窗口。" };
  }
  const months = [...new Set(seasonality.peakMonths.filter((month) => month >= 1 && month <= 12))].sort((a, b) => a - b);
  if (months.length === 0) {
    return { kind: "unknown", reason: "月度分布里没有可用月份，给不出售卖窗口。" };
  }
  if (seasonality.observedYears < 2) {
    // 关键诚实点：一年数据里的"高峰"是这一年的实情，**不是**季节性。
    return {
      kind: "unknown",
      reason: `只有 ${seasonality.observedYears} 年数据，不足以证明季节性；这一年高峰在 ${months.join("、")} 月只能说明这一年如此。`,
    };
  }
  const scope = `证据覆盖 ${seasonality.observedYears} 年`;
  return months.includes(currentMonth)
    ? { kind: "now", note: `${scope}，${months.join("、")} 月处于旺季，现在是 ${currentMonth} 月。` }
    : { kind: "window", months, note: `${scope}，旺季在 ${months.join("、")} 月，现在是 ${currentMonth} 月。` };
}

export function deriveSelectionConclusion(input: SelectionConclusionInput): SelectionConclusion {
  const dimensions: SelectionDimensionVerdict[] = [
    dimensionOf("demand", input.signals),
    dimensionOf("competition", input.signals),
    dimensionOf("price_band", input.signals),
    profitDimension(input.profit),
  ];
  const blocking = dimensions.filter((item) => item.status === "against").map((item) => item.finding);
  const missing = dimensions
    .filter((item) => item.status !== "supports" && item.status !== "against")
    .map((item) => `${SELECTION_DIMENSION_LABELS[item.key]}：${item.missing ?? "还没有带来源的依据。"}`);
  const supports = dimensions.filter((item) => item.status === "supports");
  const currentMonth = utcMonth(input.now);
  const timing = timingOf(input.seasonality, currentMonth);

  /**
   * 判据顺序即优先级，每一条都能单独解释：
   * 1. 有硬证据反对（毛利为负）→ 明确不合适卖；
   * 2. 规则判定信息不足，或**一维依据都没有** → 判不了（规则没有意见 ≠ 现在好卖）；
   * 3. 有需求依据 + 结构化窗口且当前不在窗口内 → 先别卖（**没有需求依据时不许用窗口下结论**：
   *    那是拿时间顶替需求）；
   * 4. 窗口正处旺季，或四维全有依据 → 可以卖；
   * 5. 其余 → 判不了，并点名缺的那几维。
   */
  const decision: SelectionDecision =
    blocking.length > 0 ? "do_not_sell"
      : input.verdict === "insufficient" || supports.length === 0 ? "insufficient"
        : timing.kind === "window" && dimensions[0]!.status === "supports" ? "wait"
          : timing.kind === "now" || supports.length === dimensions.length ? "sell_now"
            : "insufficient";

  const marketLabels = supports.filter((item) => item.key !== "profit").map((item) => SELECTION_DIMENSION_LABELS[item.key]);
  const profitable = dimensions[3]!.status === "supports";
  const headline =
    decision === "do_not_sell" ? `现在不合适卖：${blocking[0]}`
      : decision === "wait" ? `现在不合适卖：证据指向的售卖窗口是 ${timing.kind === "window" ? timing.months.join("、") : ""} 月，现在是 ${currentMonth} 月。`
        : decision === "sell_now" ? `现在可以卖：${marketLabels.join("、")}都有带来源的依据${profitable ? `，且按当前输入核算毛利为正（毛利率 ${input.profit?.marginRate ?? "未知"}%）` : ""}。`
          : `现在判不了合不合适卖：${[
            ...(input.verdict === "insufficient" ? ["选品初筛判定为信息不足（基础资料或平台规则还不齐）"] : []),
            ...(missing.length > 0 ? [`${missing.map((item) => item.split("：")[0]).join("、")}还没有带来源的依据`] : []),
          ].join("；")}。`;

  return SelectionConclusionSchema.parse({
    decision,
    headline: quote(headline, HEADLINE_MAX),
    timing,
    dimensions,
    blocking,
    missing,
    decidedAt: input.now,
  });
}
